import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { CurrencyExchange, Close as CloseIcon, WarningAmber } from '@mui/icons-material';
import { useState } from 'react';
import SlideUpTransition from '../../components/SlideUpTransition';
import {
    ArtistSubscriptionSettings,
    SubscriptionIntent,
    User,
} from '../../database/user';

interface ArtistSubscriptionSettingsEditorProps {
    user: User;
    subscriptionSettings?: ArtistSubscriptionSettings;
    subscriptionIntent?: SubscriptionIntent;
    setSubscriptionIntent?: (intent: SubscriptionIntent | undefined) => void;
    newSubscriptionPrice?: number;
    setNewSubscriptionPrice?: (price: number) => void;
    newSubscriptionLabel?: string;
    setnewSubscriptionLabel?: (name: string) => void;
    errors?: Record<string, string>;
    disabled?: boolean;
}

const ArtistSubscriptionSettingsEditor: React.FC<
    ArtistSubscriptionSettingsEditorProps
> = ({
    user,
    subscriptionSettings,
    subscriptionIntent,
    setSubscriptionIntent,
    newSubscriptionPrice,
    setNewSubscriptionPrice,
    newSubscriptionLabel,
    setnewSubscriptionLabel,
    errors,
    disabled,
}) => {
    const theme = useTheme();
    const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

    const isActive = subscriptionSettings?.activePriceId !== undefined;
    const activePrice =
        subscriptionSettings?.subscriptionPriceInfo?.[subscriptionSettings.activePriceId]
            ?.price;

    const isToggleChecked =
        subscriptionIntent === 'put' || (subscriptionIntent === undefined && isActive);

    const handleToggleChange = (checked: boolean) => {
        if (!setSubscriptionIntent) return;

        if (checked) {
            setSubscriptionIntent('put');
            setNewSubscriptionPrice?.(activePrice ?? 0);
        } else if (isActive) {
            // Show confirmation dialog when deactivating an active subscription
            setShowDeactivateDialog(true);
        } else {
            setSubscriptionIntent(undefined);
        }
    };

    const handleDeactivateConfirm = () => {
        if (setSubscriptionIntent) {
            setSubscriptionIntent('delete');
            setNewSubscriptionPrice?.(0);
        }
        setShowDeactivateDialog(false);
    };

    const handleDeactivateCancel = () => {
        setShowDeactivateDialog(false);
    };

    const showTextField = !disabled && isToggleChecked;

    if (!user.isArtist) {
        return null;
    }

    return (
        <>
            <Stack spacing={2}>
                <Stack sx={{ scrollMarginTop: 'calc(var(--navbar-height) + 8px)' }}>
                    <Typography variant='h5'>
                        <CurrencyExchange sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        Subscription Settings
                    </Typography>
                    <Divider />
                </Stack>

                {!disabled && setSubscriptionIntent && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isToggleChecked}
                                onChange={(e) => handleToggleChange(e.target.checked)}
                            />
                        }
                        label={
                            isToggleChecked
                                ? 'Disable Premium Subscription'
                                : 'Enable Premium Subscription'
                        }
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                fontFamily: 'LemonMilk',
                                fontSize: '1.2rem',
                            },
                        }}
                    />
                )}

                {showTextField && (
                    <>
                        <TextField
                            label='Product Name'
                            type='text'
                            value={newSubscriptionLabel || ''}
                            onChange={(e) => {
                                setnewSubscriptionLabel?.(e.target.value);
                                if (setSubscriptionIntent) setSubscriptionIntent('put');
                            }}
                            error={!!errors?.productName}
                            helperText={
                                errors?.productName || 'Set the subscription product name'
                            }
                            placeholder='Premium Subscription'
                            fullWidth
                        />
                        <TextField
                            label='Monthly Price'
                            type='number'
                            value={newSubscriptionPrice ?? activePrice ?? ''}
                            onChange={(e) => {
                                setNewSubscriptionPrice?.(
                                    parseFloat(e.target.value) || 0
                                );
                                if (setSubscriptionIntent) setSubscriptionIntent('put');
                            }}
                            error={!!errors?.subscriptionPrice}
                            helperText={
                                errors?.subscriptionPrice ||
                                'Set the monthly subscription price'
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Typography color='secondary'>$</Typography>
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                step: '0.01',
                                min: '0',
                            }}
                            sx={{ width: '120px' }}
                        />
                    </>
                )}

                {disabled && (
                    <>
                        <Typography variant='body1'>
                            The subscription is currently{' '}
                            {isActive ? 'active' : 'inactive'}.
                        </Typography>
                        {isActive && (
                            <>
                                <Typography variant='body1'>
                                    Product Name:{' '}
                                    {subscriptionSettings?.productName ||
                                        'Premium Subscription'}
                                </Typography>
                                <Typography variant='body1'>
                                    Price: ${activePrice ?? 0} per month
                                </Typography>
                            </>
                        )}
                    </>
                )}
            </Stack>

            <Dialog
                open={showDeactivateDialog}
                onClose={handleDeactivateCancel}
                TransitionComponent={SlideUpTransition}
                PaperProps={{
                    sx: {
                        borderColor: 'rgba(211, 47, 47, 0.4)',
                        boxShadow: '0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px rgba(211,47,47,0.2)',
                    },
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmber sx={{ color: 'error.main', fontSize: '1.3rem' }} />
                    Deactivate Premium Subscription?
                    <IconButton
                        aria-label='close'
                        onClick={handleDeactivateCancel}
                        sx={{ position: 'absolute', right: 10, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: theme.palette.secondary.main }}>
                        Are you sure you want to deactivate your premium subscription? All
                        user subscriptions will be canceled immediately after you save
                        your changes.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color='secondary' onClick={handleDeactivateCancel}>Cancel</Button>
                    <Button color='error' variant='contained' onClick={handleDeactivateConfirm}>
                        Deactivate
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ArtistSubscriptionSettingsEditor;
